import React, { useState, useEffect } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import Modal from '../Modal';
import { 
  getAssetFieldSchema, 
  parseAttributes, 
  stringifyAttributes,
  generateVehicleName,
  AssetTypes 
} from '../../config/assetTemplates';

/**
 * AssetForm - Modal form for creating/editing customer assets
 * 
 * Dynamically generates form fields based on asset type schema.
 * Stores custom attributes in AttributesJson for flexibility.
 */
const AssetForm = ({
  isOpen,
  onClose,
  onSave,
  asset = null, // Existing asset for editing, null for new
  defaultAssetType = AssetTypes.Other,
  availableAssetTypes = [],
  customerName = '',
}) => {
  // Form state
  const [assetType, setAssetType] = useState(defaultAssetType);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [attributes, setAttributes] = useState({});
  
  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Get schema for current asset type
  const schema = getAssetFieldSchema(assetType);

  // Initialize form when opening or asset changes
  useEffect(() => {
    if (isOpen) {
      if (asset) {
        // Editing existing asset
        setAssetType(asset.assetType);
        setName(asset.name);
        setDescription(asset.description || '');
        setIsPrimary(asset.isPrimary || false);
        setAttributes(parseAttributes(asset.attributesJson));
      } else {
        // New asset
        setAssetType(defaultAssetType);
        setName('');
        setDescription('');
        setIsPrimary(false);
        setAttributes({});
      }
      setError(null);
    }
  }, [isOpen, asset, defaultAssetType]);

  // Track if user has manually edited the name
  const [nameManuallyEdited, setNameManuallyEdited] = useState(false);

  // Auto-generate name for vehicles (only if not manually edited)
  useEffect(() => {
    if (assetType === AssetTypes.Vehicle && !nameManuallyEdited) {
      const generatedName = generateVehicleName(attributes);
      if (generatedName !== 'Unnamed Vehicle') {
        setName(generatedName);
      }
    }
  }, [attributes, assetType, nameManuallyEdited]);

  // Auto-generate name for Person type using customer name
  useEffect(() => {
    if (!asset && assetType === AssetTypes.Person && !name && customerName) {
      setName(`${customerName}'s Profile`);
    }
  }, [assetType, customerName, name, asset]);

  // Handle attribute change
  const handleAttributeChange = (key, value) => {
    setAttributes(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle type change - reset attributes
  const handleTypeChange = (newType) => {
    setAssetType(newType);
    setAttributes({});
    setName('');
  };

  // Handle save
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave({
        assetType,
        name: name.trim(),
        description: description.trim() || null,
        attributesJson: stringifyAttributes(attributes),
        isPrimary,
        sortOrder: asset?.sortOrder || 0,
      });
    } catch (err) {
      console.error('Error saving asset:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save');
      setSaving(false);
    }
  };

  // Render a field based on its type
  const renderField = (field) => {
    const value = attributes[field.key] ?? '';
    const id = `asset-field-${field.key}`;

    switch (field.type) {
      case 'select':
        return (
          <select
            id={id}
            value={value}
            onChange={(e) => handleAttributeChange(field.key, e.target.value)}
            className="input"
          >
            <option value="">Select...</option>
            {field.options?.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );

      case 'textarea':
        return (
          <textarea
            id={id}
            value={value}
            onChange={(e) => handleAttributeChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            className="input"
            rows={3}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            id={id}
            value={value}
            onChange={(e) => handleAttributeChange(field.key, e.target.value ? Number(e.target.value) : '')}
            placeholder={field.placeholder}
            className="input"
          />
        );

      case 'date':
        return (
          <input
            type="date"
            id={id}
            value={value}
            onChange={(e) => handleAttributeChange(field.key, e.target.value)}
            className="input"
          />
        );

      default: // text
        return (
          <input
            type="text"
            id={id}
            value={value}
            onChange={(e) => handleAttributeChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            className="input"
          />
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={asset ? `Edit ${schema.displayName}` : `Add ${schema.displayName}`}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Error display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Asset Type selector - only if multiple types available */}
        {availableAssetTypes.length > 1 && !asset && (
          <div>
            <label className="label">Asset Type</label>
            <div className="flex flex-wrap gap-2">
              {availableAssetTypes.map(type => {
                const typeSchema = getAssetFieldSchema(type);
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleTypeChange(type)}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      assetType === type
                        ? 'bg-primary-50 border-primary-300 text-primary-700'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {typeSchema.displayName}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Name field */}
        <div>
          <label htmlFor="asset-name" className="label">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="asset-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameManuallyEdited(true);
            }}
            placeholder={schema.namePlaceholder}
            className="input"
            required
          />
          {schema.nameHelpText && (
            <p className="text-xs text-gray-500 mt-1">{schema.nameHelpText}</p>
          )}
        </div>

        {/* Dynamic attribute fields */}
        {schema.fields && schema.fields.length > 0 && (
          <div className="border-t border-gray-200 pt-4 mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              {schema.displayName} Details
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {schema.fields.map(field => (
                <div 
                  key={field.key} 
                  className={field.type === 'textarea' ? 'sm:col-span-2' : ''}
                >
                  <label htmlFor={`asset-field-${field.key}`} className="label text-sm">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {renderField(field)}
                  {field.helpText && (
                    <p className="text-xs text-gray-500 mt-1">{field.helpText}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Description field */}
        <div>
          <label htmlFor="asset-description" className="label">
            Notes <span className="text-xs text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea
            id="asset-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Any additional notes about this asset..."
            className="input"
            rows={2}
          />
        </div>

        {/* Primary checkbox */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="asset-primary"
            checked={isPrimary}
            onChange={(e) => setIsPrimary(e.target.checked)}
            className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <label htmlFor="asset-primary" className="text-sm text-gray-700">
            Set as primary {schema.displayName.toLowerCase()}
            <span className="text-gray-400 ml-1">(for quick selection)</span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary flex-1 justify-center"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary flex-1 justify-center flex items-center gap-2"
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                {asset ? 'Save Changes' : `Add ${schema.displayName}`}
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AssetForm;